import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { TaskCardMolecule } from '../../TaskCard';
import type { StatusValue } from '../../StatusCheckInDialog/CheckInFormContent';
import { Task } from '../../TaskList/TaskListMolecule.types';
import { useToolbarContextOrNull } from '../../../../context/ToolbarContext';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import './TasksMode.scss';

/** Maps task status values to their localization keys */
const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: 'arc.stage.task.pending',
  in_progress: 'arc.stage.task.in_progress',
  complete: 'arc.stage.task.complete',
  overdue: 'arc.stage.task.overdue',
};

export interface TasksModeProps {
  /** Active sphere ID to fetch tasks for */
  activeSphereId: string;
  /** Additional sphere IDs (orbitals) - future enhancement */
  additionalSphereIds?: string[];
  /** Callback when a task is clicked */
  onTaskClick?: (task: Task) => void;
  /** Callback when check-in action is triggered */
  onCheckIn?: (task: Task) => void;
  /** Callback to open the sphere editor for a workflow task */
  onEditSphere?: (taskId: string, task: Task) => void;
  /** Filter tasks by status (e.g. 'pending', 'in_progress', 'complete', 'overdue') */
  statusFilter?: string | null;
  /** Callback to clear the active status filter */
  onClearFilter?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TasksMode - Full task feed view
 *
 * Displays tasks from the unified tasks API as a scrollable feed.
 * Includes workflow tasks and check-in tasks.
 */
const TasksMode: React.FC<TasksModeProps> = ({
  activeSphereId: _activeSphereId,
  additionalSphereIds: _additionalSphereIds,
  onTaskClick,
  onCheckIn,
  onEditSphere,
  statusFilter,
  onClearFilter,
  className = '',
}) => {
  const { t } = useTranslation();

  // Use shared data from context (no duplicate API calls)
  const toolbarContext = useToolbarContextOrNull();
  const apiClients = useApiClientsOrNull();

  // No-op function for when context is not available
  const noopAsync = async (): Promise<void> => { /* no-op */ };

  // Get current user info and tasks from shared context
  const currentUserId = toolbarContext?.currentUserId ?? undefined;
  const currentUserName = toolbarContext?.currentUserName ?? undefined;
  const tasks = toolbarContext?.taskState.tasks ?? [];
  const loading = toolbarContext?.taskState.isLoading ?? false;
  const error = toolbarContext?.taskState.error ?? null;
  const refresh = toolbarContext?.refreshTasks ?? noopAsync;

  // Inline check-in accordion state
  const [expandedCheckInTaskId, setExpandedCheckInTaskId] = useState<string | null>(null);
  const [checkInSuccessTaskId, setCheckInSuccessTaskId] = useState<string | null>(null);
  const [isCheckInSubmitting, setIsCheckInSubmitting] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll animation - track which cards have animated in
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollObserverRef = useRef<IntersectionObserver | null>(null);

  // Clean up success flash timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Set up Intersection Observer for scroll animations
  useEffect(() => {
    scrollObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = (entry.target as HTMLElement).dataset.cardId;
            if (cardId) {
              setVisibleCards((prev) => new Set(prev).add(cardId));
              // Unobserve after animating in (animation only happens once)
              scrollObserverRef.current?.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px 0px -50px 0px', // trigger slightly before entering viewport
        threshold: 0.1,
      }
    );

    return () => {
      scrollObserverRef.current?.disconnect();
    };
  }, []);

  const filteredTasks = useMemo(() => {
    if (!statusFilter) return tasks;
    return tasks.filter((task) => task.status === statusFilter);
  }, [tasks, statusFilter]);

  // Observe cards when they mount
  useEffect(() => {
    const observer = scrollObserverRef.current;
    if (!observer) return;

    cardRefs.current.forEach((element) => {
      observer.observe(element);
    });
  }, [filteredTasks]);

  // Callback to register card refs
  const setCardRef = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(id, element);
      scrollObserverRef.current?.observe(element);
    } else {
      const el = cardRefs.current.get(id);
      if (el) {
        scrollObserverRef.current?.unobserve(el);
        cardRefs.current.delete(id);
      }
    }
  }, []);

  const isFilterActive = !!statusFilter;

  const handleTaskClick = useCallback(
    (task: Task) => {
      onTaskClick?.(task);
    },
    [onTaskClick]
  );

  const handleCheckInExpandChange = useCallback(
    (taskId: string, expanded: boolean) => {
      setCheckInError(null);
      if (expanded) {
        setExpandedCheckInTaskId(taskId);
      } else {
        setExpandedCheckInTaskId(null);
      }
    },
    []
  );

  const handleCheckInSubmit = useCallback(
    async (statusValue: StatusValue, comment?: string) => {
      if (!apiClients?.graphClient || !expandedCheckInTaskId) return;

      const task = tasks.find((t) => t.id === expandedCheckInTaskId);
      if (!task?.factorId || !task?.sphereId) return;

      setIsCheckInSubmitting(true);
      setCheckInError(null);

      try {
        await apiClients.graphClient.post(
          `/sphere/${task.sphereId}/factors/${task.factorId}/checkin`,
          { statusValue, comment }
        );

        // Collapse form
        setExpandedCheckInTaskId(null);

        // Show success flash for 1.5s
        setCheckInSuccessTaskId(task.id);
        if (successTimerRef.current) clearTimeout(successTimerRef.current);
        successTimerRef.current = setTimeout(() => {
          setCheckInSuccessTaskId(null);
        }, 1500);

        // Refresh task list
        await toolbarContext?.refreshTasks();

        // Notify parent (for analytics or side effects)
        onCheckIn?.(task);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[TasksMode] Check-in failed:', message);
        setCheckInError(t('checkIn.inline.errorMessage'));
      } finally {
        setIsCheckInSubmitting(false);
      }
    },
    [apiClients?.graphClient, expandedCheckInTaskId, tasks, toolbarContext, onCheckIn, t]
  );

  // Loading state
  if (loading) {
    return (
      <div className={`tasks-mode tasks-mode--loading ${className}`}>
        <div className="tasks-mode__loading">
          <i className="pi pi-spin pi-spinner" />
          <span>{t('taskList.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`tasks-mode tasks-mode--error ${className}`}>
        <div className="tasks-mode__error">
          <i className="pi pi-exclamation-circle" />
          <span>{error}</span>
          <Button
            label={t('common.retry')}
            icon="pi pi-refresh"
            onClick={() => refresh()}
            size="small"
            outlined
          />
        </div>
      </div>
    );
  }

  // Empty state (no tasks at all, regardless of filter)
  if (tasks.length === 0) {
    return (
      <div className={`tasks-mode tasks-mode--empty ${className}`}>
        <div className="tasks-mode__empty">
          <i className="pi pi-check-circle" />
          <span>{t('taskList.noTasks')}</span>
        </div>
      </div>
    );
  }

  const statusLabel = statusFilter
    ? t(STATUS_LABEL_KEYS[statusFilter] || statusFilter)
    : '';

  return (
    <div className={`tasks-mode ${className}`}>
      {/* Panel title */}
      <div className="tasks-mode__title-bar">
        <i className="pi pi-check-square" />
        <span>{t('toolbar.tasks')}</span>
      </div>

      <div className="tasks-mode__header">
        <span className="tasks-mode__count">
          {filteredTasks.length} {t('taskList.tasks')}
        </span>
        <Button
          icon="pi pi-refresh"
          onClick={() => refresh()}
          size="small"
          text
          rounded
          aria-label={t('taskList.refresh')}
        />
      </div>

      {isFilterActive && (
        <div className="tasks-mode__filter-bar">
          <span className="tasks-mode__filter-label">
            {t('tasksMode.filterActive', { status: statusLabel })}
          </span>
          <button
            className="tasks-mode__filter-clear"
            onClick={onClearFilter}
            aria-label={t('tasksMode.clearFilter')}
            type="button"
          >
            <i className="pi pi-times" />
          </button>
        </div>
      )}

      {filteredTasks.length === 0 && isFilterActive ? (
        <div className="tasks-mode tasks-mode--empty">
          <div className="tasks-mode__empty">
            <i className="pi pi-filter-slash" />
            <span>{t('tasksMode.noFilterResults', { status: statusLabel })}</span>
          </div>
        </div>
      ) : (
        <div className="tasks-mode__feed">
          {filteredTasks.map((task, index) => {
            const isVisible = visibleCards.has(task.id);
            const animationDelay = `${Math.min(index * 50, 300)}ms`; // Max 300ms delay

            return (
              <div
                key={task.id}
                ref={(el) => setCardRef(task.id, el)}
                data-card-id={task.id}
                className={`tasks-mode__card-wrapper ${isVisible ? 'visible' : ''}`}
                style={{ animationDelay }}
              >
                <TaskCardMolecule
                  task={task}
                  currentUserId={currentUserId}
                  currentUserName={currentUserName}
                  onEditSphere={onEditSphere}
                  onClick={handleTaskClick}
                  isCheckInExpanded={expandedCheckInTaskId === task.id}
                  onCheckInExpandChange={handleCheckInExpandChange}
                  checkInSuccess={checkInSuccessTaskId === task.id}
                  onCheckInSubmit={handleCheckInSubmit}
                  isCheckInSubmitting={isCheckInSubmitting && expandedCheckInTaskId === task.id}
                  checkInError={expandedCheckInTaskId === task.id ? checkInError : null}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TasksMode;
